import { z } from 'zod';
import {
  DEFAULT_HOME_LAYOUT_CONTENT,
  HOMEPAGE_CANVAS_COLUMNS,
  HOMEPAGE_SINGLETON_SECTION_TYPES,
  HOMEPAGE_SECTION_REGISTRY,
  normalizeHomepageSectionSettings,
  type CanvasSectionBlock,
  type CanvasSectionBlockLayout,
  type CanvasSectionBlockType,
  type CanvasSectionSettings,
  type HomepageLayoutContent,
  type HomepageLayoutSection,
  type HomepageSectionSettings,
  type HomepageSectionType,
} from '../components/sections/sectionRegistry';
import {
  clampSectionMinRows,
  normalizeCanvasGridResolution,
  SECTION_MIN_ROWS_DEFAULT,
  SECTION_MIN_ROWS_MAX,
  SECTION_MIN_ROWS_MIN,
} from '../components/sections/layoutEngine/sectionSizing';
import { ensureResponsiveLayoutSlots } from '../components/sections/layoutEngine/blockFrame';
import { ensureResponsiveTransformSlots } from '../components/sections/layoutEngine/transformStyle';
import { normalizeCollectionCardActionContent } from './collectionCardActions';
import { normalizeGalleryBlockContent } from './productGallery';
import {
  FLOW_VALUE_TYPES,
  normalizeFlowDefinitions,
  normalizePageFlowState,
} from './flows';
import { normalizeFlowTriggerBindings } from './flowTriggerNormalization';
import { upsertProductCardFlowNodesForSections } from './productCardFlow';
import { upsertProductGalleryFlowNodesForSections } from './productGalleryFlow';
import { upsertProductDeconstructorFlowNodesForSections } from './productDeconstructorFlow';
import { upsertRequestFormFlowNodesForSections } from './requestFormFlow';
import { upsertCustomiserBlockFlowNodesForSections } from './customiserBlockFlow';
import { upsertButtonFlowNodesForSections } from './buttonFlow';
import { stripStepTrackerRuntimeState } from './progressContent';
import { resolveStagePageSections } from './stageLayoutRules';
import type { FlowScope, FlowValueType } from '../types';

const MAX_LAYOUT_ROWS = 512;
const SECTION_TYPES = Object.keys(HOMEPAGE_SECTION_REGISTRY) as HomepageSectionType[];
const BLOCK_TYPES: CanvasSectionBlockType[] = [
  'text',
  'image',
  'decorative-asset',
  'button',
  'video',
  'social',
  'site-progress',
  'site-footer',
  'gallery',
  'product-card',
  'model-viewer',
  'product-deconstructor',
  'request-form',
  'customiser-viewer',
  'customiser-finish-picker',
  'customiser-colour-filter',
  'customiser-combination-results',
  'spacer',
];
const CANVAS_BLOCK_ID_FALLBACK_PREFIX = 'block';
const SECTION_ID_FALLBACK_PREFIX = 'section';
const FLOW_SCOPES: FlowScope[] = ['page', 'section'];

export type LayoutValidationMode = 'load' | 'save' | 'publish';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const clampGridValue = (value: unknown, min: number, max: number, fallback: number) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  const rounded = Math.round(numeric);
  return Math.max(min, Math.min(max, rounded));
};

const blockLayoutSchema = z
  .object({
    x: z.number().int().min(1).max(HOMEPAGE_CANVAS_COLUMNS),
    y: z.number().int().min(1).max(MAX_LAYOUT_ROWS),
    w: z.number().int().min(1).max(HOMEPAGE_CANVAS_COLUMNS),
    h: z.number().int().min(1).max(MAX_LAYOUT_ROWS),
  })
  .superRefine((layout, ctx) => {
    if (layout.x + layout.w - 1 > HOMEPAGE_CANVAS_COLUMNS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `block layout exceeds ${HOMEPAGE_CANVAS_COLUMNS}-column grid`,
        path: ['w'],
      });
    }
  });

const blockContentSchema = z.record(z.string(), z.unknown());
const buttonShadowSchema = z
  .object({
    x: z.number().min(-50).max(50).optional(),
    y: z.number().min(-50).max(50).optional(),
    blur: z.number().min(0).max(100).optional(),
    spread: z.number().min(-20).max(50).optional(),
    color: z.string().optional(),
  })
  .partial();

const buttonTextShadowSchema = z
  .object({
    x: z.number().min(-20).max(20).optional(),
    y: z.number().min(-20).max(20).optional(),
    blur: z.number().min(0).max(40).optional(),
    color: z.string().optional(),
  })
  .partial();

const buttonTextGlowSchema = z
  .object({
    blur: z.number().min(0).max(60).optional(),
    color: z.string().optional(),
    opacity: z.number().min(0).max(1).optional(),
  })
  .partial();

const buttonTextReliefSchema = z
  .object({
    enabled: z.boolean().optional(),
    highlightColor: z.string().optional(),
    shadowColor: z.string().optional(),
    distance: z.number().min(0).max(12).optional(),
    blur: z.number().min(0).max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
  })
  .partial();

const buttonBorderSideSchema = z
  .object({
    width: z.number().min(0).max(10).optional(),
    color: z.string().optional(),
    style: z.enum(['solid', 'dashed', 'dotted', 'none']).optional(),
  })
  .partial();

const buttonVisualStateSchema = z
  .object({
    backgroundColor: z.string().optional(),
    backgroundOpacity: z.number().min(0).max(1).optional(),
    borderColor: z.string().optional(),
    borderWidth: z.number().min(0).max(10).optional(),
    borderStyle: z.enum(['solid', 'dashed', 'dotted', 'none']).optional(),
    borderRadius: z.number().min(0).max(50).optional(),
    borderLinked: z.boolean().optional(),
    borderSides: z
      .object({
        top: buttonBorderSideSchema.optional(),
        right: buttonBorderSideSchema.optional(),
        bottom: buttonBorderSideSchema.optional(),
        left: buttonBorderSideSchema.optional(),
      })
      .partial()
      .optional(),
    boxShadow: buttonShadowSchema.optional(),
    textColor: z.string().optional(),
    fontFamilyKey: z.string().optional(),
    fontSize: z.number().min(8).max(1000).optional(),
    fontSizeMobile: z.number().min(8).max(1000).optional(),
    fontSizeDesktop: z.number().min(8).max(1000).optional(),
    fontWeight: z.number().min(100).max(900).optional(),
    fontStyle: z.enum(['normal', 'italic']).optional(),
    textDecorationLine: z.enum(['none', 'underline']).optional(),
    textTransform: z.enum(['none', 'uppercase', 'lowercase', 'capitalize']).optional(),
    textAlign: z.enum(['left', 'center', 'right']).optional(),
    textRole: z.enum(['body', 'heading', 'wordmark']).optional(),
    textSizingMode: z.enum(['frame', 'scale']).optional(),
    lineHeight: z.number().min(1).max(2.2).optional(),
    letterSpacing: z.number().min(-2).max(12).optional(),
    wordSpacing: z.number().min(-8).max(40).optional(),
    textOpacity: z.number().min(0).max(1).optional(),
    textBlendMode: z.enum(['normal', 'multiply', 'screen', 'overlay', 'soft-light', 'difference', 'exclusion']).optional(),
    textShadow: buttonTextShadowSchema.optional(),
    textFillMode: z.enum(['solid', 'hollow', 'image', 'gradient']).optional(),
    textFillImageUrl: z.string().optional(),
    textFillImageScale: z.number().min(10).max(400).optional(),
    textFillImagePositionX: z.number().min(-50).max(50).optional(),
    textFillImagePositionY: z.number().min(-50).max(50).optional(),
    textGradientStartColor: z.string().optional(),
    textGradientEndColor: z.string().optional(),
    textGradientAngle: z.number().min(0).max(360).optional(),
    textStrokeColor: z.string().optional(),
    textStrokeWidth: z.number().min(0).max(8).optional(),
    textOuterGlow: buttonTextGlowSchema.optional(),
    textInnerGlow: buttonTextGlowSchema.optional(),
    textEffects: z
      .object({
        shadow: z.boolean().optional(),
        outerGlow: z.boolean().optional(),
        innerGlow: z.boolean().optional(),
        emboss: z.boolean().optional(),
        bevel: z.boolean().optional(),
        pressed: z.boolean().optional(),
      })
      .partial()
      .optional(),
    textEmboss: buttonTextReliefSchema.optional(),
    textBevel: buttonTextReliefSchema.optional(),
    textPressed: buttonTextReliefSchema.optional(),
    textEffectPreset: z.enum(['none', 'emboss', 'bevel', 'pressed']).optional(),
    imageFit: z.enum(['cover', 'contain', 'fill']).optional(),
    imageScale: z.number().min(10).max(300).optional(),
    imageScaleX: z.number().min(0.1).max(5).optional(),
    imageScaleY: z.number().min(0.1).max(5).optional(),
    imageRotation: z.number().min(-3600).max(3600).optional(),
    imagePositionX: z.number().min(-100).max(100).optional(),
    imagePositionY: z.number().min(-100).max(100).optional(),
    imageFlipX: z.boolean().optional(),
    imageFlipY: z.boolean().optional(),
    sizingMode: z.enum(['hug', 'stretch']).optional(),
    visualHeight: z.number().min(24).max(96).optional(),
    alignX: z.enum(['left', 'center', 'right']).optional(),
    alignY: z.enum(['top', 'center', 'bottom']).optional(),
    paddingX: z.number().min(0).max(64).optional(),
    paddingY: z.number().min(0).max(40).optional(),
  })
  .partial();

const blockStyleSchema = buttonVisualStateSchema
  .extend({
    hover: buttonVisualStateSchema.optional(),
  })
  .catchall(z.unknown());

const canvasBlockSchema = z
  .object({
    id: z.string().min(1),
    type: z.enum(BLOCK_TYPES as [CanvasSectionBlockType, ...CanvasSectionBlockType[]]),
    layout: blockLayoutSchema,
    responsiveLayout: z
      .object({
        tablet: blockLayoutSchema.optional(),
        mobile: blockLayoutSchema.optional(),
      })
      .partial()
      .optional(),
    content: blockContentSchema.optional(),
    style: blockStyleSchema.optional(),
  })
  .superRefine((block, ctx) => {
    if (block.type === 'gallery') {
      const galleryContent = block.content;
      if (!galleryContent) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'gallery blocks require content',
          path: ['content'],
        });
      } else {
        const galleryMode = galleryContent.galleryMode === 'products' ? 'products' : 'media';
        if (galleryMode !== 'products') {
          const itemsRaw = galleryContent.items;
          if (!Array.isArray(itemsRaw)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'gallery blocks require items to be an array',
              path: ['content', 'items'],
            });
          } else {
            itemsRaw.forEach((item, index) => {
              const itemRecord = isRecord(item) ? item : null;
              if (!itemRecord || typeof itemRecord.src !== 'string') {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'gallery image item requires src string',
                  path: ['content', 'items', index, 'src'],
                });
              }
            });
          }
        }

        if (
          galleryContent.spacing !== undefined &&
          (!Number.isFinite(galleryContent.spacing) ||
            Number(galleryContent.spacing) < 0 ||
            Number(galleryContent.spacing) > 64)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'gallery spacing must be a number between 0 and 64',
            path: ['content', 'spacing'],
          });
        }

        if (
          galleryContent.loop !== undefined &&
          typeof galleryContent.loop !== 'boolean'
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'gallery loop must be a boolean',
            path: ['content', 'loop'],
          });
        }
      }
    }

    if (block.style?.__template_slot === 'collection' && block.style?.role === 'vase-card') {
      const content = block.content;
      if (!content) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'collection vase cards require content',
          path: ['content'],
        });
      } else {
        const primaryAction = content.primaryAction;
        const secondaryAction = content.secondaryAction;
        const actionEntries = [
          ['primaryAction', primaryAction],
          ['secondaryAction', secondaryAction],
        ] as const;

        actionEntries.forEach(([field, value]) => {
          if (!isRecord(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field} must be an object`,
              path: ['content', field],
            });
            return;
          }
          if (value.action !== 'selectVase' && value.action !== 'viewModel') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field}.action must be selectVase or viewModel`,
              path: ['content', field, 'action'],
            });
          }
          if (typeof value.target !== 'string') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field}.target must be a string`,
              path: ['content', field, 'target'],
            });
          }
        });
      }
    }

    const style = block.style;
    if (!style) return;
    if (style.__protected_component === true) {
      const instance = style.__protected_component_instance;
      if (typeof instance !== 'string' || !instance.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'protected blocks require __protected_component_instance metadata',
          path: ['style', '__protected_component_instance'],
        });
      }
    }
    if (style.__template_slot !== undefined && (typeof style.__template_slot !== 'string' || !style.__template_slot.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '__template_slot must be a non-empty string when present',
        path: ['style', '__template_slot'],
      });
    }

    if (block.type === 'product-card') {
      const content = block.content;
      if (!content) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'product cards require content',
          path: ['content'],
        });
      } else {
        const primaryAction = content.primaryAction;
        const secondaryAction = content.secondaryAction;
        const actionEntries = [
          ['primaryAction', primaryAction],
          ['secondaryAction', secondaryAction],
        ] as const;

        actionEntries.forEach(([field, value]) => {
          if (!isRecord(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field} must be an object`,
              path: ['content', field],
            });
            return;
          }
          if (value.action !== 'selectVase' && value.action !== 'viewModel') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field}.action must be selectVase or viewModel`,
              path: ['content', field, 'action'],
            });
          }
          if (typeof value.target !== 'string') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field}.target must be a string`,
              path: ['content', field, 'target'],
            });
          }
        });
      }
    }
  });

const canvasSettingsSchema = z.object({
  minRows: z.number().int().min(1).max(SECTION_MIN_ROWS_MAX),
  minRowsMode: z.enum(['auto', 'manual']).optional(),
  presentation: z.enum(['default', 'immersive']).optional(),
  blocks: z.array(canvasBlockSchema),
});

const eventsSettingsSchema = z.object({
  heading: z.string().optional(),
  description: z.string().optional(),
  upcomingTitle: z.string().optional(),
  pastTitle: z.string().optional(),
  emptyTitle: z.string().optional(),
  emptyDescription: z.string().optional(),
  showHomeLink: z.boolean().optional(),
  homeLinkLabel: z.string().optional(),
  showPastEvents: z.boolean().optional(),
  pastMonths: z.number().int().min(1).max(24).optional(),
  pastLimit: z.number().int().min(1).max(24).optional(),
});

const sectionSettingsSchema = z.object({
  builderLabel: z.string().min(1).optional(),
  progressStepId: z.string().min(1).optional(),
  padding: z.enum(['none', 'sm', 'md', 'lg', 'xl']).optional(),
  paddingTop: z.enum(['none', 'sm', 'md', 'lg', 'xl']).optional(),
  paddingBottom: z.enum(['none', 'sm', 'md', 'lg', 'xl']).optional(),
  maxWidth: z.enum(['full', 'xl', 'lg', 'md']).optional(),
  background: z.enum(['default', 'muted', 'accent']).optional(),
  builderSeedTemplate: z
    .object({
      kind: z.literal('built-in'),
      id: z.string().min(1),
      label: z.string().min(1),
    })
    .optional(),
  builderTemplate: z
    .object({
      kind: z.literal('saved'),
      id: z.string().min(1),
    })
    .optional(),
  canvas: canvasSettingsSchema.optional(),
  events: eventsSettingsSchema.optional(),
  collectionCardTemplates: z.array(z.record(z.string(), z.unknown())).optional(),
});

const layoutSectionSchema = z.object({
  type: z.enum(SECTION_TYPES as [HomepageSectionType, ...HomepageSectionType[]]),
  id: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
  settings: sectionSettingsSchema.optional(),
});

const flowFieldSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(FLOW_VALUE_TYPES as [FlowValueType, ...FlowValueType[]]),
  multiple: z.boolean().optional(),
});

const flowNodeBindingSchema = z.object({
  socketId: z.string().min(1),
  fieldId: z.string().min(1),
});

const flowNodeSchema = z.object({
  id: z.string().min(1),
  blockId: z.string().min(1),
  blockType: z.string().min(1),
  adapterId: z.string().min(1).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  inputs: z.array(flowNodeBindingSchema),
  outputs: z.array(flowNodeBindingSchema),
});

const flowConnectionSchema = z.object({
  id: z.string().min(1),
  fromNodeId: z.string().min(1),
  fromSocketId: z.string().min(1),
  toNodeId: z.string().min(1),
  toSocketId: z.string().min(1),
  fieldId: z.string().min(1),
});

const flowDefinitionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  scope: z.enum(FLOW_SCOPES as [FlowScope, ...FlowScope[]]),
  sectionId: z.string().min(1).optional(),
  presetId: z.string().min(1).optional(),
  fields: z.array(flowFieldSchema),
  nodes: z.array(flowNodeSchema),
  connections: z.array(flowConnectionSchema),
});

const pageFlowStateEntrySchema = z.object({
  values: z.record(z.string(), z.unknown()),
  updatedAt: z.string().min(1),
});

const homepageLayoutSchema = z.object({
  version: z.number().int().optional(),
  flows: z.array(flowDefinitionSchema).optional(),
  flowState: z.record(z.string(), pageFlowStateEntrySchema).optional(),
  sections: z.array(layoutSectionSchema),
});

function normalizeBlockStyle(style: unknown): Record<string, unknown> | undefined {
  if (!isRecord(style)) return undefined;
  const next: Record<string, unknown> = { ...style };

  if (next.__builder_anchor !== undefined) {
    next.__builder_anchor = next.__builder_anchor === true;
  }
  if (next.__layout_locked !== undefined) {
    next.__layout_locked = next.__layout_locked === true;
  }
  if (next.__protected_component !== undefined) {
    next.__protected_component = next.__protected_component === true;
  }
  if (next.__template_slot !== undefined && typeof next.__template_slot !== 'string') {
    delete next.__template_slot;
  }
  if (next.role !== undefined && typeof next.role !== 'string') {
    delete next.role;
  }
  if (next.backgroundOpacity !== undefined) {
    next.backgroundOpacity = Math.max(0, Math.min(1, Number(next.backgroundOpacity) || 0));
  }
  if (next.borderWidth !== undefined) {
    next.borderWidth = Math.max(0, Math.min(10, Number(next.borderWidth) || 0));
  }
  if (next.borderRadius !== undefined) {
    next.borderRadius = Math.max(0, Math.min(50, Number(next.borderRadius) || 0));
  }
  if (next.borderLinked !== undefined) {
    next.borderLinked = next.borderLinked === true;
  }
  if (next.fontSize !== undefined) {
    next.fontSize = Math.max(8, Math.min(1000, Number(next.fontSize) || 8));
  }
  if (next.fontSizeMobile !== undefined) {
    next.fontSizeMobile = Math.max(8, Math.min(1000, Number(next.fontSizeMobile) || 8));
  }
  if (next.fontSizeDesktop !== undefined) {
    next.fontSizeDesktop = Math.max(8, Math.min(1000, Number(next.fontSizeDesktop) || 8));
  }
  if (next.fontWeight !== undefined) {
    next.fontWeight = Math.max(100, Math.min(900, Number(next.fontWeight) || 400));
  }
  if (next.fontFamilyKey !== undefined && typeof next.fontFamilyKey !== 'string') {
    delete next.fontFamilyKey;
  }
  if (
    next.textTransform !== undefined &&
    next.textTransform !== 'none' &&
    next.textTransform !== 'uppercase' &&
    next.textTransform !== 'lowercase' &&
    next.textTransform !== 'capitalize'
  ) {
    delete next.textTransform;
  }
  if (
    next.textAlign !== undefined &&
    next.textAlign !== 'left' &&
    next.textAlign !== 'center' &&
    next.textAlign !== 'right'
  ) {
    delete next.textAlign;
  }
  if (
    next.textRole !== undefined &&
    next.textRole !== 'body' &&
    next.textRole !== 'heading' &&
    next.textRole !== 'wordmark'
  ) {
    delete next.textRole;
  }
  if (
    next.textSizingMode !== undefined &&
    next.textSizingMode !== 'frame' &&
    next.textSizingMode !== 'scale'
  ) {
    delete next.textSizingMode;
  }
  if (next.lineHeight !== undefined) {
    next.lineHeight = Math.max(1, Math.min(2.2, Number(next.lineHeight) || 1));
  }
  if (next.letterSpacing !== undefined) {
    next.letterSpacing = Math.max(-2, Math.min(12, Number(next.letterSpacing) || 0));
  }
  if (next.wordSpacing !== undefined) {
    next.wordSpacing = Math.max(-8, Math.min(40, Number(next.wordSpacing) || 0));
  }
  if (next.sizingMode === 'fill') {
    next.sizingMode = 'stretch';
  }
  if (next.sizingMode === 'fit') {
    next.sizingMode = 'hug';
  }
  if (
    next.sizingMode !== undefined &&
    next.sizingMode !== 'hug' &&
    next.sizingMode !== 'stretch'
  ) {
    delete next.sizingMode;
  }
  if (next.visualHeight !== undefined) {
    const visualHeightPresets: Record<string, number> = {
      sm: 32,
      md: 40,
      default: 48,
      lg: 56,
      xl: 64,
    };
    const presetValue =
      typeof next.visualHeight === 'string' ? visualHeightPresets[next.visualHeight] : undefined;
    next.visualHeight = Math.max(24, Math.min(96, Number(presetValue ?? next.visualHeight) || 48));
  }
  if (next.alignX === 'stretch') {
    next.alignX = 'center';
  }
  if (
    next.alignX !== undefined &&
    next.alignX !== 'left' &&
    next.alignX !== 'center' &&
    next.alignX !== 'right'
  ) {
    delete next.alignX;
  }
  if (next.alignY === 'stretch') {
    next.alignY = 'center';
  }
  if (
    next.alignY !== undefined &&
    next.alignY !== 'top' &&
    next.alignY !== 'center' &&
    next.alignY !== 'bottom'
  ) {
    delete next.alignY;
  }
  if (next.textOpacity !== undefined) {
    next.textOpacity = Math.max(0, Math.min(1, Number(next.textOpacity) || 0));
  }
  if (
    next.textBlendMode !== undefined &&
    next.textBlendMode !== 'normal' &&
    next.textBlendMode !== 'multiply' &&
    next.textBlendMode !== 'screen' &&
    next.textBlendMode !== 'overlay' &&
    next.textBlendMode !== 'soft-light' &&
    next.textBlendMode !== 'difference' &&
    next.textBlendMode !== 'exclusion'
  ) {
    delete next.textBlendMode;
  }
  if (next.paddingX !== undefined) {
    next.paddingX = Math.max(0, Math.min(64, Number(next.paddingX) || 0));
  }
  if (next.paddingY !== undefined) {
    next.paddingY = Math.max(0, Math.min(40, Number(next.paddingY) || 0));
  }
  if (next.marginX !== undefined) {
    next.marginX = Math.max(0, Math.min(64, Number(next.marginX) || 0));
  }
  if (next.marginY !== undefined) {
    next.marginY = Math.max(0, Math.min(40, Number(next.marginY) || 0));
  }
  if (next.boxShadow && isRecord(next.boxShadow)) {
    next.boxShadow = {
      x: Math.max(-50, Math.min(50, Number(next.boxShadow.x) || 0)),
      y: Math.max(-50, Math.min(50, Number(next.boxShadow.y) || 0)),
      blur: Math.max(0, Math.min(100, Number(next.boxShadow.blur) || 0)),
      spread: Math.max(-20, Math.min(50, Number(next.boxShadow.spread) || 0)),
      color: typeof next.boxShadow.color === 'string' ? next.boxShadow.color : '',
    };
  }
  if (next.textShadow && isRecord(next.textShadow)) {
    next.textShadow = {
      x: Math.max(-20, Math.min(20, Number(next.textShadow.x) || 0)),
      y: Math.max(-20, Math.min(20, Number(next.textShadow.y) || 0)),
      blur: Math.max(0, Math.min(40, Number(next.textShadow.blur) || 0)),
      color: typeof next.textShadow.color === 'string' ? next.textShadow.color : '',
    };
  }
  if (
    next.textFillMode !== undefined &&
    next.textFillMode !== 'solid' &&
    next.textFillMode !== 'hollow' &&
    next.textFillMode !== 'image' &&
    next.textFillMode !== 'gradient'
  ) {
    delete next.textFillMode;
  }
  if (next.textFillImageUrl !== undefined && typeof next.textFillImageUrl !== 'string') {
    delete next.textFillImageUrl;
  }
  if (next.textFillImageScale !== undefined) {
    next.textFillImageScale = Math.max(10, Math.min(400, Number(next.textFillImageScale) || 100));
  }
  if (next.textFillImagePositionX !== undefined) {
    next.textFillImagePositionX = Math.max(-50, Math.min(50, Number(next.textFillImagePositionX) || 0));
  }
  if (next.textFillImagePositionY !== undefined) {
    next.textFillImagePositionY = Math.max(-50, Math.min(50, Number(next.textFillImagePositionY) || 0));
  }
  if (next.textGradientStartColor !== undefined && typeof next.textGradientStartColor !== 'string') {
    delete next.textGradientStartColor;
  }
  if (next.textGradientEndColor !== undefined && typeof next.textGradientEndColor !== 'string') {
    delete next.textGradientEndColor;
  }
  if (next.textGradientAngle !== undefined) {
    next.textGradientAngle = Math.max(0, Math.min(360, Number(next.textGradientAngle) || 0));
  }
  if (next.textStrokeColor !== undefined && typeof next.textStrokeColor !== 'string') {
    delete next.textStrokeColor;
  }
  if (next.textStrokeWidth !== undefined) {
    next.textStrokeWidth = Math.max(0, Math.min(8, Number(next.textStrokeWidth) || 0));
  }
  if (
    next.imageFit !== undefined &&
    next.imageFit !== 'cover' &&
    next.imageFit !== 'contain' &&
    next.imageFit !== 'fill'
  ) {
    delete next.imageFit;
  }
  if (next.imageScale !== undefined) {
    next.imageScale = Math.max(10, Math.min(300, Number(next.imageScale) || 100));
  }
  if (next.imageScaleX !== undefined) {
    next.imageScaleX = Math.max(0.1, Math.min(5, Number(next.imageScaleX) || 1));
  }
  if (next.imageScaleY !== undefined) {
    next.imageScaleY = Math.max(0.1, Math.min(5, Number(next.imageScaleY) || 1));
  }
  if (next.imageRotation !== undefined) {
    next.imageRotation = Math.max(-3600, Math.min(3600, Number(next.imageRotation) || 0));
  }
  if (next.imagePositionX !== undefined) {
    next.imagePositionX = Math.max(-100, Math.min(100, Number(next.imagePositionX) || 0));
  }
  if (next.imagePositionY !== undefined) {
    next.imagePositionY = Math.max(-100, Math.min(100, Number(next.imagePositionY) || 0));
  }
  if (next.imageFlipX !== undefined) {
    next.imageFlipX = next.imageFlipX === true;
  }
  if (next.imageFlipY !== undefined) {
    next.imageFlipY = next.imageFlipY === true;
  }
  if (next.textOuterGlow && isRecord(next.textOuterGlow)) {
    next.textOuterGlow = {
      blur: Math.max(0, Math.min(60, Number(next.textOuterGlow.blur) || 0)),
      color: typeof next.textOuterGlow.color === 'string' ? next.textOuterGlow.color : '',
      opacity: Math.max(0, Math.min(1, Number(next.textOuterGlow.opacity) || 0)),
    };
  }
  if (next.textInnerGlow && isRecord(next.textInnerGlow)) {
    next.textInnerGlow = {
      blur: Math.max(0, Math.min(60, Number(next.textInnerGlow.blur) || 0)),
      color: typeof next.textInnerGlow.color === 'string' ? next.textInnerGlow.color : '',
      opacity: Math.max(0, Math.min(1, Number(next.textInnerGlow.opacity) || 0)),
    };
  }
  if (next.textEffects && isRecord(next.textEffects)) {
    next.textEffects = {
      shadow: typeof next.textEffects.shadow === 'boolean' ? next.textEffects.shadow : undefined,
      outerGlow: typeof next.textEffects.outerGlow === 'boolean' ? next.textEffects.outerGlow : undefined,
      innerGlow: typeof next.textEffects.innerGlow === 'boolean' ? next.textEffects.innerGlow : undefined,
      emboss: typeof next.textEffects.emboss === 'boolean' ? next.textEffects.emboss : undefined,
      bevel: typeof next.textEffects.bevel === 'boolean' ? next.textEffects.bevel : undefined,
      pressed: typeof next.textEffects.pressed === 'boolean' ? next.textEffects.pressed : undefined,
    };
  }
  const normalizeTextRelief = (value: unknown) => {
    if (!isRecord(value)) return undefined;
    return {
      enabled: typeof value.enabled === 'boolean' ? value.enabled : undefined,
      highlightColor: typeof value.highlightColor === 'string' ? value.highlightColor : '',
      shadowColor: typeof value.shadowColor === 'string' ? value.shadowColor : '',
      distance: Math.max(0, Math.min(12, Number(value.distance) || 0)),
      blur: Math.max(0, Math.min(20, Number(value.blur) || 0)),
      opacity: Math.max(0, Math.min(1, Number(value.opacity) || 0)),
    };
  };
  if (next.textEmboss) {
    next.textEmboss = normalizeTextRelief(next.textEmboss);
  }
  if (next.textBevel) {
    next.textBevel = normalizeTextRelief(next.textBevel);
  }
  if (next.textPressed) {
    next.textPressed = normalizeTextRelief(next.textPressed);
  }
  if (
    next.textEffectPreset !== undefined &&
    next.textEffectPreset !== 'none' &&
    next.textEffectPreset !== 'emboss' &&
    next.textEffectPreset !== 'bevel' &&
    next.textEffectPreset !== 'pressed'
  ) {
    delete next.textEffectPreset;
  }
  if (next.borderSides && isRecord(next.borderSides)) {
    const normalizeBorderSide = (value: unknown) => {
      if (!isRecord(value)) return undefined;
      return {
        width: Math.max(0, Math.min(10, Number(value.width) || 0)),
        color: typeof value.color === 'string' ? value.color : '',
        style:
          value.style === 'solid' ||
          value.style === 'dashed' ||
          value.style === 'dotted' ||
          value.style === 'none'
            ? value.style
            : 'solid',
      };
    };
    next.borderSides = {
      top: normalizeBorderSide(next.borderSides.top),
      right: normalizeBorderSide(next.borderSides.right),
      bottom: normalizeBorderSide(next.borderSides.bottom),
      left: normalizeBorderSide(next.borderSides.left),
    };
  }
  if (next.hover && isRecord(next.hover)) {
    next.hover = normalizeBlockStyle(next.hover) ?? {};
  }

  if (next.__protected_component === true) {
    const hasInstance =
      typeof next.__protected_component_instance === 'string' &&
      next.__protected_component_instance.trim().length > 0;
    if (!hasInstance) {
      next.__protected_component_instance =
        typeof next.role === 'string' && next.role.trim().length > 0
          ? next.role.trim()
          : 'protected-component';
    }
  }

  return next;
}

function normalizeBlockLayout(layout: unknown): CanvasSectionBlockLayout {
  const source = isRecord(layout) ? layout : {};
  const w = clampGridValue(source.w, 1, HOMEPAGE_CANVAS_COLUMNS, 4);
  let x = clampGridValue(source.x, 1, HOMEPAGE_CANVAS_COLUMNS, 1);
  if (x + w - 1 > HOMEPAGE_CANVAS_COLUMNS) {
    x = Math.max(1, HOMEPAGE_CANVAS_COLUMNS - w + 1);
  }
  const h = clampGridValue(source.h, 1, MAX_LAYOUT_ROWS, 2);
  const y = clampGridValue(source.y, 1, MAX_LAYOUT_ROWS, 1);
  return { x, y, w, h };
}

function normalizeResponsiveBlockLayout(layout: unknown): CanvasSectionBlock['responsiveLayout'] {
  if (!isRecord(layout)) return undefined;
  const tablet = isRecord(layout.tablet) ? normalizeBlockLayout(layout.tablet) : undefined;
  const mobile = isRecord(layout.mobile) ? normalizeBlockLayout(layout.mobile) : undefined;
  if (!tablet && !mobile) return undefined;
  return {
    ...(tablet ? { tablet } : {}),
    ...(mobile ? { mobile } : {}),
  };
}

function normalizeCanvasBlock(
  block: unknown,
  sectionType: HomepageSectionType,
  index: number
): CanvasSectionBlock | null {
  if (!isRecord(block)) return null;
  const type = block.type;
  if (!BLOCK_TYPES.includes(type as CanvasSectionBlockType)) return null;

  const blockId =
    typeof block.id === 'string' && block.id.trim()
      ? block.id.trim()
      : `${CANVAS_BLOCK_ID_FALLBACK_PREFIX}-${sectionType}-${index + 1}`;
  const style = normalizeBlockStyle(block.style);
  const rawContent = isRecord(block.content) ? { ...block.content } : undefined;
  const modelReference =
    rawContent && Number.isFinite(rawContent.modelReference) && Number(rawContent.modelReference) > 0
      ? Number(rawContent.modelReference)
      : Number(blockId.match(/\d+/)?.[0] ?? 0);
  const modelPath =
    rawContent && typeof rawContent.modelPath === 'string' ? rawContent.modelPath : undefined;
  const content =
    type === 'gallery'
      ? normalizeGalleryBlockContent(block.content)
      : type === 'product-card' && rawContent
        ? normalizeCollectionCardActionContent(
            rawContent,
            modelReference,
            modelPath
          )
      : style?.__template_slot === 'collection' && style.role === 'vase-card' && rawContent
        ? normalizeCollectionCardActionContent(rawContent, modelReference, modelPath)
        : rawContent;

  return ensureResponsiveLayoutSlots({
    id: blockId,
    type: type as CanvasSectionBlockType,
    layout: normalizeBlockLayout(block.layout),
    responsiveLayout: normalizeResponsiveBlockLayout(block.responsiveLayout),
    content,
    style: ensureResponsiveTransformSlots(style),
  });
}

function normalizeCanvasSettings(
  canvas: unknown,
  sectionType: HomepageSectionType
): CanvasSectionSettings | undefined {
  if (!isRecord(canvas)) return undefined;
  const rawBlocks = Array.isArray(canvas.blocks) ? canvas.blocks : [];
  const blocks = rawBlocks
    .map((block, index) => normalizeCanvasBlock(block, sectionType, index))
    .filter((block): block is CanvasSectionBlock => Boolean(block));

  return normalizeCanvasGridResolution(
    {
      minRows: canvas.minRows,
      minRowsMode: canvas.minRowsMode === 'manual' ? 'manual' : 'auto',
      presentation: canvas.presentation === 'immersive' ? 'immersive' : 'default',
      blocks,
    },
    {
      floorRows: SECTION_MIN_ROWS_MIN,
      fallbackRows: SECTION_MIN_ROWS_DEFAULT,
    }
  );
}

function normalizeSectionSettings(
  settings: unknown,
  sectionType: HomepageSectionType
): HomepageSectionSettings | undefined {
  const normalized = normalizeHomepageSectionSettings(settings, {
    sectionType,
  });
  if (!normalized) return undefined;
  const canvas = normalizeCanvasSettings(normalized.canvas, sectionType);
  if (!canvas) return normalized;
  return {
    ...normalized,
    canvas,
  };
}

function normalizeLayoutSections(
  rawSections: unknown,
  options: { includeDisabled: boolean; fallbackSections: HomepageLayoutSection[] }
): HomepageLayoutSection[] {
  if (!Array.isArray(rawSections)) {
    return options.fallbackSections;
  }

  if (rawSections.length === 0) {
    return [];
  }

  const sections: HomepageLayoutSection[] = [];
  const seenSingletons = new Set<HomepageSectionType>();

  for (let index = 0; index < rawSections.length; index += 1) {
    const raw = rawSections[index];
    if (!isRecord(raw)) continue;
    const type = raw.type;
    if (!SECTION_TYPES.includes(type as HomepageSectionType)) continue;

    const sectionType = type as HomepageSectionType;
    if (HOMEPAGE_SINGLETON_SECTION_TYPES.has(sectionType)) {
      if (seenSingletons.has(sectionType)) continue;
      seenSingletons.add(sectionType);
    }

    const enabled = raw.enabled !== false;
    if (!options.includeDisabled && !enabled) continue;

    const sectionId =
      typeof raw.id === 'string' && raw.id.trim()
        ? raw.id.trim()
        : `${SECTION_ID_FALLBACK_PREFIX}-${sectionType}-${index + 1}`;

    sections.push({
      type: sectionType,
      id: sectionId,
      enabled,
      settings: normalizeSectionSettings(raw.settings, sectionType),
    });
  }

  const resolvedSections = sections.length > 0 ? sections : options.fallbackSections;
  return resolveStagePageSections(resolvedSections);
}

function normalizeLayoutInput(
  value: unknown,
  options: { includeDisabled: boolean; fallback?: HomepageLayoutContent }
): HomepageLayoutContent {
  const fallback = options.fallback ?? DEFAULT_HOME_LAYOUT_CONTENT;
  const raw = isRecord(value) ? value : {};
  const sections = normalizeLayoutSections(raw.sections, {
    includeDisabled: options.includeDisabled,
    fallbackSections: fallback.sections,
  });
  const flows = upsertRequestFormFlowNodesForSections(
    upsertProductDeconstructorFlowNodesForSections(
      upsertProductGalleryFlowNodesForSections(
      upsertProductCardFlowNodesForSections(
        upsertCustomiserBlockFlowNodesForSections(
          upsertButtonFlowNodesForSections(
            normalizeFlowTriggerBindings(normalizeFlowDefinitions(raw.flows)),
            sections
          ),
          sections
        ),
        sections
      ),
      sections
    ),
      sections
    ),
    sections
  );
  const flowState = stripStepTrackerRuntimeState(normalizePageFlowState(raw.flowState, flows));

  return {
    version:
      typeof raw.version === 'number' && Number.isFinite(raw.version)
        ? Math.round(raw.version)
        : fallback.version,
    flows,
    flowState,
    sections,
  };
}

function formatIssues(error: z.ZodError) {
  return error.issues.map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : 'layout';
    return `${path}: ${issue.message}`;
  });
}

function warnValidation(mode: LayoutValidationMode, pageKey: string, issues: string[]) {
  if (!import.meta.env.DEV || issues.length === 0) return;
  console.warn(
    `[layoutValidation:${mode}] ${pageKey} -> ${issues.join(' | ')}`
  );
}

export type LayoutValidationResult = {
  layout: HomepageLayoutContent;
  issues: string[];
  fatal: boolean;
};

export function validateAndNormalizeHomepageLayout(
  value: unknown,
  options: {
    mode: LayoutValidationMode;
    pageKey: string;
    includeDisabled?: boolean;
    fallback?: HomepageLayoutContent;
  }
): LayoutValidationResult {
  const includeDisabled = options.includeDisabled ?? true;
  const normalized = normalizeLayoutInput(value, {
    includeDisabled,
    fallback: options.fallback,
  });

  const parsed = homepageLayoutSchema.safeParse(normalized);
  if (parsed.success) {
    return {
      layout: parsed.data as HomepageLayoutContent,
      issues: [],
      fatal: false,
    };
  }

  const issues = formatIssues(parsed.error);
  warnValidation(options.mode, options.pageKey, issues);

  if (options.mode === 'load') {
    return {
      layout: normalized,
      issues,
      fatal: false,
    };
  }

  return {
    layout: normalized,
    issues,
    fatal: true,
  };
}

export const homepageLayoutValidationSchema = homepageLayoutSchema;
export const homepageLayoutSectionValidationSchema = layoutSectionSchema;
export const homepageBlockValidationSchema = canvasBlockSchema;
export const homepageBlockLayoutValidationSchema = blockLayoutSchema;
